import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Cierre suave para lo que se superpone (modales, paneles, desplegables).
 *
 * EL PROBLEMA: React quita el nodo del DOM en cuanto el estado dice que ya no
 * va, asi que una animacion de salida escrita en CSS no llega a verse nunca.
 * Todo entraba con calma y desaparecia de golpe, que es justo el contraste que
 * hace que una interfaz se sienta barata.
 *
 * COMO FUNCIONA: `pedirCierre()` no desmonta; marca `cerrando`, el componente
 * se pinta con la clase `is-closing`, y solo cuando la animacion TERMINA de
 * verdad se llama a `onClose`.
 *
 * Se espera al evento `animationend` en vez de a un `setTimeout` con el numero
 * copiado del CSS. Dos motivos:
 *
 *   - No hay dos fuentes de verdad. Cambiar la duracion en la hoja de estilos
 *     no deja aqui un temporizador desincronizado que corte la animacion por la
 *     mitad o deje el modal muerto en pantalla.
 *   - Con `prefers-reduced-motion` las animaciones duran .01ms y el evento
 *     llega de inmediato: el cierre es instantaneo sin escribir un caso aparte.
 *
 * El temporizador de respaldo existe porque `animationend` no se dispara si el
 * elemento no llega a tener animacion (una regla que no cargo, un navegador que
 * la ignora). Sin el, el panel se quedaria abierto para siempre -- prefiero un
 * cierre sin animar que un cierre que no ocurre.
 */
const RESPALDO_MS = 400;

export function useCierreSuave(onClose, abierto = true) {
  const [cerrando, setCerrando] = useState(false);
  const ref = useRef(null);
  const hecho = useRef(false);

  /*
   * Al REABRIR se vuelve a empezar.
   *
   * `hecho` existe para que el cierre no se dispare dos veces (el evento de fin
   * de animacion y el temporizador de respaldo compiten). Pero se quedaba puesto
   * para siempre, y eso solo funciona si el componente se desmonta al cerrarse.
   *
   * Los que devuelven `null` en vez de desmontarse -- el panel de cuenta, el
   * aviso de Pro, la confirmacion de borrado -- conservaban el `hecho` de la vez
   * anterior: se cerraban bien la primera vez y a partir de la segunda el aspa,
   * Escape y pulsar fuera dejaban de hacer nada. El dialogo se quedaba pegado.
   *
   * Por eso el hook necesita saber si esta abierto: no para decidir que pintar,
   * sino para saber cuando empieza un ciclo nuevo.
   */
  useEffect(() => {
    if (!abierto) return;
    hecho.current = false;
    setCerrando(false);
  }, [abierto]);

  const pedirCierre = useCallback(() => {
    if (hecho.current) return;
    setCerrando(true);
  }, []);

  useEffect(() => {
    if (!cerrando) return;
    const cerrar = () => {
      if (hecho.current) return;
      hecho.current = true;
      onClose?.();
    };
    const nodo = ref.current;
    // Solo cuenta la animacion del propio elemento: las de dentro (una lista
    // que escalona su entrada) tambien burbujean y cerrarian antes de tiempo.
    const alAcabar = (e) => e.target === nodo && cerrar();
    nodo?.addEventListener('animationend', alAcabar);
    const respaldo = setTimeout(cerrar, RESPALDO_MS);
    return () => {
      nodo?.removeEventListener('animationend', alAcabar);
      clearTimeout(respaldo);
    };
  }, [cerrando, onClose]);

  return { ref, cerrando, pedirCierre };
}

/**
 * El gesto "atras" cierra el modal en vez de sacar del sitio.
 *
 * La aplicacion no navega entre rutas, asi que para el navegador solo existia
 * una pagina: cualquier "atras" -- el gesto del borde en el telefono, sobre
 * todo -- abandonaba PokeTrack aunque hubiera tres capas abiertas. Este hook
 * apila una entrada de historial al abrirse el modal y lo cierra cuando esa
 * entrada se saca (popstate); "atras" pasa a deshacer capas, que es lo que
 * todo el mundo espera en el movil.
 *
 * COMO CASA CON EL CIERRE POR INTERFAZ (aspa, Escape, pulsar fuera): en ese
 * caso la entrada apilada sigue en el historial y hay que consumirla, o el
 * siguiente "atras" seria un paso muerto. La limpieza del efecto llama a
 * `history.back()`... con un retraso de un tick, y ahi esta la sutileza:
 *
 * StrictMode monta-desmonta-monta en desarrollo. Si la limpieza hiciera el
 * back() en el acto, el remontaje apilaria una SEGUNDA entrada y el back
 * pendiente la sacaria al instante: todo modal se cerraria solo nada mas
 * abrirse (solo en dev, que es donde se mira). Con el back() aplazado a un
 * temporizador, el remontaje inmediato de StrictMode lo cancela y ADOPTA la
 * entrada que ya estaba apilada (la ref sobrevive al remontaje); en un
 * desmontaje de verdad no hay remontaje que lo cancele y el back() sale.
 *
 * Con modales apilados (carta encima de la hoja del mazo), cada entrada lleva
 * su profundidad: al venir un popstate solo cierra quien quedo POR ENCIMA de
 * la profundidad en la que se aterrizo, una capa por "atras".
 *
 * `cerrar` va en ref y no en dependencias: suele ser una arrow recreada en
 * cada render y reejecutar el efecto por eso apilaria entradas sin fin.
 */
export function useVolverCierra(abierto, cerrar) {
  const refCerrar = useRef(cerrar);
  refCerrar.current = cerrar;
  const viaPop = useRef(false);
  const timerBack = useRef(null);
  const miEntrada = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;

    let mia;
    if (timerBack.current != null) {
      // Remontaje de StrictMode: se cancela el back() pendiente y se adopta
      // la entrada que la pasada anterior dejo apilada.
      clearTimeout(timerBack.current);
      timerBack.current = null;
      mia = miEntrada.current;
    } else {
      mia = (window.history.state?.modal ?? 0) + 1;
      window.history.pushState({ ...(window.history.state ?? {}), modal: mia }, '');
      miEntrada.current = mia;
    }

    const onPop = () => {
      if ((window.history.state?.modal ?? 0) < mia) {
        viaPop.current = true;
        miEntrada.current = null;
        refCerrar.current?.();
      }
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      if (viaPop.current) {
        viaPop.current = false;
        return;
      }
      timerBack.current = setTimeout(() => {
        timerBack.current = null;
        miEntrada.current = null;
        /*
         * Solo se saca la entrada si ARRIBA sigue estando la propia. Si el
         * desmontaje vino de navegar de verdad -- pulsar una pestaña del
         * navbar con la capa abierta --, para cuando este back() despierta la
         * entrada de arriba ya es la de la pestaña nueva (sin `modal`), y
         * sacarla se comeria esa navegacion: pulsabas Meta y aterrizabas de
         * vuelta en "mis mazos". La entrada propia queda enterrada en el
         * historial (un "atras" repetido mas tarde), que es un precio menor
         * que robar la navegacion que se acaba de pedir.
         */
        if ((window.history.state?.modal ?? 0) >= mia) window.history.back();
      }, 0);
    };
  }, [abierto]);
}

/**
 * Traspaso de un modal a otro: cerrar el que esta y abrir el siguiente.
 *
 * EL BUG QUE EVITA: cerrar con `pedirCierre()` y abrir el siguiente modal en el
 * mismo clic deja dos entradas de historial apiladas. La del modal saliente no
 * se consume hasta que termina su animacion y se desmonta (el back() aplazado
 * de `useVolverCierra`), y para entonces el modal entrante ya apilo la suya
 * ENCIMA. Ese back() tardio saca la entrada de arriba -- la del modal nuevo --
 * y su propio useVolverCierra lo cierra: el panel de cuenta pestañeaba y moria
 * a medio segundo de abrirse, justo en el flujo de crear cuenta.
 *
 * COMO: se cierra por la via del historial, que es el mismo camino que el gesto
 * "atras": el back() de aqui consume YA la entrada del modal abierto, su
 * popstate lo cierra con animacion y marca `viaPop` (no queda back pendiente).
 * El siguiente se abre en ese mismo popstate -- este listener corre despues del
 * del modal porque se registro despues -- y apila sobre un historial limpio.
 */
export function traspasarModal(abrirSiguiente) {
  window.addEventListener('popstate', () => abrirSiguiente(), { once: true });
  window.history.back();
}

/**
 * Foco confinado al modal, y devuelto al cerrar.
 *
 * Dos deudas de teclado que tenian todos los modales de la aplicacion:
 *
 *   - Tab se salia del dialogo y seguia recorriendo la pagina de detras, que
 *     esta tapada pero sigue en el DOM. Para quien navega con teclado o con
 *     lector, el modal era un agujero del que se cae sin querer.
 *   - Al cerrar, el foco se quedaba huerfano en <body> en vez de volver al
 *     boton que abrio el dialogo, con lo que habia que recorrer la pagina
 *     desde arriba tras cada consulta.
 *
 * El hook confina Tab/Shift+Tab a lo enfocable de dentro (en circulo, como los
 * dialogos nativos), pone el foco dentro al abrir si no lo esta ya -- los que
 * quieren enfocar algo concreto, como "Cancelar" en la confirmacion, lo hacen
 * ellos y este respeta lo hecho -- y lo devuelve a quien lo tenia al cerrar.
 *
 * `ref` es el MISMO que usa `useCierreSuave`: el nodo del dialogo.
 */
const FOCABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocoModal(ref, activo = true) {
  useEffect(() => {
    if (!activo) return;
    const previo = document.activeElement;

    // Al abrir, el foco entra al dialogo si nadie lo puso ya dentro.
    const nodo = ref.current;
    if (nodo && !nodo.contains(document.activeElement)) {
      nodo.querySelector(FOCABLE)?.focus();
    }

    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const el = ref.current;
      if (!el) return;
      // Solo lo visible: un boton en una pestaña plegada no debe recibir foco.
      const items = [...el.querySelectorAll(FOCABLE)].filter((x) => x.offsetParent !== null);
      if (!items.length) return;
      const primero = items[0];
      const ultimo = items[items.length - 1];
      if (!el.contains(document.activeElement)) {
        e.preventDefault();
        primero.focus();
      } else if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };
    // En captura: gana a cualquier manejador de la pagina de detras.
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (previo?.focus && document.contains(previo)) previo.focus();
    };
  }, [ref, activo]);
}

/**
 * Entrada escalonada de una lista.
 *
 * Devuelve el `style` para el elemento en la posicion `i`. Que las filas no
 * aparezcan todas a la vez sino con unos milisegundos entre ellas hace que la
 * lista se lea como algo que llega, no como algo que parpadea.
 *
 * El escalonado se CORTA a los `max` primeros: en una tabla de 60 arquetipos,
 * seguir sumando retraso dejaria la ultima fila entrando casi un segundo tarde,
 * y para entonces ya no se lee como elegancia sino como que la pagina va lenta.
 */
export function retraso(i, { paso = 22, max = 12 } = {}) {
  return { animationDelay: `${Math.min(i, max) * paso}ms` };
}

/**
 * REORDENAR SIN SALTOS (la tecnica FLIP).
 *
 * EL PROBLEMA. La lista del mazo se ordena por copias, asi que quitar una copia
 * a un 4-de lo manda de golpe varias filas mas abajo. La fila desaparece de
 * donde estabas mirando y aparece en otro sitio en el mismo fotograma: hay que
 * volver a buscarla con la vista para comprobar que el cambio es el que
 * querias. Es el momento en que mas se necesita seguir una fila y es justo
 * cuando la interfaz la pierde.
 *
 * COMO FUNCIONA. Se apunta donde estaba cada fila (First), se deja que React
 * pinte donde va ahora (Last), se calcula la diferencia (Invert) y se anima de
 * vuelta a cero (Play). El navegador ya ha hecho el trabajo de colocar; esto
 * solo cuenta el viaje.
 *
 * SE ANIMA CON LA WEB ANIMATIONS API y no con una transicion CSS a proposito:
 * estas filas ya tienen un `animation` de entrada que toca `transform`, y
 * meter ahi una transicion sobre la misma propiedad es pedir que se peleen.
 * `el.animate()` corre fuera de esa via, no deja estilos inline que limpiar
 * despues y va acelerada por hardware.
 *
 * Devuelve un registrador de referencias: `registrar(id)` da el `ref` de esa
 * fila. Al desmontarse se borra sola del mapa, asi que no se acumulan nodos
 * muertos entre reordenaciones.
 */
export function useReordenSuave({ duracion = 180 } = {}) {
  const nodos = useRef(new Map());
  const posiciones = useRef(new Map());
  const orden = useRef('');

  /*
   * SE MIDE EN CADA PINTADO, PERO SOLO SE ANIMA SI CAMBIO EL ORDEN.
   *
   * Los dos a la vez, y cada uno arregla un fallo distinto:
   *
   * - Medir siempre, porque el mapa de posiciones se queda VIEJO en cuanto la
   *   pagina se recoloca por cualquier otro motivo. Pasó de verdad: al entrar
   *   en modo edicion la lista se mueve entera sin que cambie el reparto de
   *   cartas, y al quitar despues una copia se comparaba contra el diseño
   *   anterior -- las 22 filas salieron animando 283 px cuando solo tres se
   *   habian movido 40.
   *
   * - Animar solo si cambio el orden, porque si no cualquier recolocacion
   *   ajena (abrir el panel del analista encima, por ejemplo) mandaria la lista
   *   entera a deslizarse sin que nadie haya tocado una carta. Cuando el orden
   *   es el mismo, esto se limita a apuntar las posiciones nuevas y callarse.
   */
  useLayoutEffect(() => {
    const previas = posiciones.current;
    const nuevas = new Map();
    const vivos = [];
    for (const [id, el] of nodos.current) {
      if (!el || !el.isConnected) continue;
      vivos.push([id, el, el.offsetTop]);
      nuevas.set(id, el.offsetTop);
    }

    const ordenNuevo = vivos.map(([id]) => id).join('|');
    const cambioElOrden = orden.current !== '' && ordenNuevo !== orden.current;
    const quieto =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (cambioElOrden && !quieto) {
      for (const [id, el, ahora] of vivos) {
        const antes = previas.get(id);
        /* Solo lo que YA estaba y ha cambiado de sitio. Una fila nueva no tiene
           "antes": esa entra con su propia animacion de siempre. */
        if (antes == null || antes === ahora) continue;
        el.animate(
          [{ transform: `translateY(${antes - ahora}px)` }, { transform: 'none' }],
          { duration: duracion, easing: 'cubic-bezier(.23, 1, .32, 1)' }
        );
      }
    }

    posiciones.current = nuevas;
    orden.current = ordenNuevo;
  });

  return (id) => (el) => {
    if (el) nodos.current.set(id, el);
    else nodos.current.delete(id);
  };
}

/**
 * ¿Casa esta media query? Reactivo: cambia al girar el teléfono o al estrechar
 * la ventana.
 *
 * Existe para lo que el CSS no puede decidir solo: DÓNDE se monta un control.
 * Esconder con `display: none` una copia y enseñar otra deja dos controles
 * vivos con el mismo estado y dos rótulos para un lector de pantalla; aquí se
 * monta UNO, en el sitio que toca a ese ancho.
 */
export function useMedia(query) {
  const leer = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [casa, setCasa] = useState(leer);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const alCambiar = () => setCasa(mq.matches);
    alCambiar();
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, [query]);
  return casa;
}
